import { useLayoutEffect, useRef, useState } from "react"

const Overlay = ({ isVideoReady }: { isVideoReady: boolean }) => {
  // Responsive scanning area dimensions
  // Using useLayoutEffect to calculate positions based on actual container size
  // Barcode scanning needs a wide horizontal rectangle with 326:300 aspect ratio
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({
    scanningWindowHeight: 0,
    topBlurHeight: 0,
    sideBlurTop: 0,
  })

  const horizontalMargin = 24 // 24px margins on each side
  const aspectRatio = 326 / 300 // Maintain 326:300 aspect ratio
  const borderRadius = "16px"

  useLayoutEffect(() => {
    const calculateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const containerHeight = containerRef.current.offsetHeight

        // Calculate scanning window width and height
        const scanningWindowWidth = containerWidth - horizontalMargin * 2
        const scanningWindowHeight = scanningWindowWidth * (300 / 326)

        // Calculate top position of scanning window (centered vertically)
        const scanningWindowTop = (containerHeight - scanningWindowHeight) / 2

        setDimensions({
          scanningWindowHeight,
          topBlurHeight: scanningWindowTop,
          sideBlurTop: scanningWindowTop,
        })
      }
    }

    calculateDimensions()
    window.addEventListener("resize", calculateDimensions)
    return () => window.removeEventListener("resize", calculateDimensions)
  }, [])

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {/* Top blur area - only blur the area above the scanning window */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: dimensions.topBlurHeight,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          pointerEvents: "none",
        }}
      />

      {/* Bottom blur area - only blur the area below the scanning window */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: dimensions.topBlurHeight,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          pointerEvents: "none",
        }}
      />

      {/* Left blur area - only blur the left side beside the scanning window */}
      <div
        style={{
          position: "absolute",
          top: dimensions.sideBlurTop,
          left: 0,
          width: horizontalMargin,
          height: dimensions.scanningWindowHeight,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          pointerEvents: "none",
        }}
      />

      {/* Right blur area - only blur the right side beside the scanning window */}
      <div
        style={{
          position: "absolute",
          top: dimensions.sideBlurTop,
          right: 0,
          width: horizontalMargin,
          height: dimensions.scanningWindowHeight,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          pointerEvents: "none",
        }}
      />

      {/* Dark overlay with rounded cutout using box-shadow */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: `calc(100% - ${horizontalMargin * 2}px)`,
          aspectRatio: `${aspectRatio}`,
          borderRadius: borderRadius,
          boxShadow: "0 0 0 2000px rgba(0, 0, 0, 0.6)",
          pointerEvents: "none",
          background: isVideoReady ? "transparent" : "rgb(102,102,102)",
        }}
      />

      {/* White border frame around scanning area - centered */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: `calc(100% - ${horizontalMargin * 2}px)`,
          aspectRatio: `${aspectRatio}`,
          border: "2px solid white",
          borderRadius: borderRadius,
          pointerEvents: "none",
          boxSizing: "border-box",
          zIndex: 2,
        }}
      />
    </div>
  )
}

export default Overlay
